// ============================================
// COMPANY ADMIN PANEL
// ============================================
// Dedicated admin interface for Company Admins
// - Manage company users
// - Configure company settings
// - Set user permissions
// - Accessible only to users with companyAdmin role

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Users, Settings, Shield, Building2 } from 'lucide-react';
import { CompanyUsers } from './CompanyUsers';
import { CompanyUserPermissions } from './CompanyUserPermissions';
import { CompanySettings } from './CompanySettings';

interface CompanyAdminPanelProps {
  accessToken: string;
  companyId: string;
  companyName: string;
  currentUser: any;
}

export function CompanyAdminPanel({
  accessToken,
  companyId,
  companyName,
  currentUser,
}: CompanyAdminPanelProps) {
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Shield className="size-6 text-purple-600" />
            </div>
            <div>
              <CardTitle>Company Administration</CardTitle>
              <CardDescription>
                Manage users, settings, and permissions for {companyName}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Admin Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="users">
            <Users className="size-4 mr-2" />
            Manage Users
          </TabsTrigger>
          <TabsTrigger value="permissions">
            <Shield className="size-4 mr-2" />
            Permissions
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="size-4 mr-2" />
            Company Settings
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <CompanyUsers
            accessToken={accessToken}
            currentUser={currentUser}
            companyId={companyId}
          />
        </TabsContent>

        {/* Permissions Tab */}
        <TabsContent value="permissions" className="space-y-4">
          <CompanyUserPermissions
            accessToken={accessToken}
            companyId={companyId}
            currentUser={currentUser}
          />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <CompanySettings
            accessToken={accessToken}
            companyId={companyId}
            companyName={companyName}
            currentUser={currentUser}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
